import {
  PRESET_TITLE_MAX_CHARACTERS,
  VERSION_NUMBER_LENGTH,
  VersionNumber,
} from "@pax/api-contract/presets/v1alpha1";
import {
  focusHaloFlush,
  HapticSurface,
  panelMotion,
  press,
  solidSurface,
  usePanelDismiss,
} from "@pax/ui";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { type AnimationEvent, useEffect, useState } from "react";
import { sessionQuery } from "@/features/auth/queries";
import { useCharacterLimit, versionNumberProblem } from "@/features/preset-editor/NewVersionDialog";
import { refreshPresets } from "@/features/preset-editor/queries";
import { api, orpc, requestErrorMessage } from "@/lib/api";
import { ScreenOverlay } from "@/shell/Screen";

/** The address a preset with this title would get, asked once typing pauses. */
function useNewPresetSlug(title: string, enabled: boolean) {
  const [settled, setSettled] = useState(title);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(title), 250);
    return () => clearTimeout(timer);
  }, [title]);
  const { data } = useQuery({
    ...orpc.presets.slug.get.queryOptions({ input: { query: { title: settled } } }),
    enabled: enabled && settled.length > 0,
    placeholderData: keepPreviousData,
  });
  return title && data ? data.slug : null;
}

/** Starts a blank preset of your own from its title and first version number. */
export function WriteScenarioPanel({
  leaving,
  onLeft,
  onClose,
}: {
  /** True from the moment it is asked to go until it has gone. */
  leaving: boolean;
  onLeft: (event: AnimationEvent<HTMLElement>) => void;
  onClose: () => void;
}) {
  // The scrim covers everything, so "outside the panel" and "on the scrim" are
  // the same place; routing presses through the trigger as well would only
  // have the trigger reopening what it closed.
  const panel = usePanelDismiss<HTMLDivElement>({
    open: !leaving,
    onDismiss: onClose,
    dismissOnOutsidePress: false,
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useQuery(sessionQuery);
  const account = Boolean(session?.user && !session.user.isAnonymous);
  const [title, setTitle] = useState("");
  const [versionNumber, setVersionNumber] = useState("");
  /** Whether the form was sent since the number last changed, so unfinished numbers say why. */
  const [submitted, setSubmitted] = useState(false);
  const numberLimit = useCharacterLimit(VERSION_NUMBER_LENGTH);
  const numberProblem = numberLimit.tooLong ?? versionNumberProblem(versionNumber, submitted);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const slug = useNewPresetSlug(title.trim(), account);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setProblem(null);
    try {
      await work();
    } catch (error) {
      setProblem(requestErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScreenOverlay>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={onClose}
        data-leaving={leaving || undefined}
        className="overlay-scrim absolute inset-0 cursor-default"
      />
      <div
        ref={panel}
        role="dialog"
        aria-label="Create Preset"
        data-leaving={leaving || undefined}
        onAnimationEnd={onLeft}
        className={`overlay-panel relative max-h-full w-full max-w-sm overflow-y-auto p-5 ${solidSurface}`}
      >
        <div className="flex items-center justify-between gap-4">
          <p className="text-base font-medium">Create Preset</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`relative flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-foreground hover:bg-foreground/15 ${panelMotion} ${press} ${focusHaloFlush}`}
          >
            <X className="size-4" />
            <HapticSurface />
          </button>
        </div>

        {!account ? (
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            A scenario has to belong to somebody. Connect an account to write one.
          </p>
        ) : (
          <form
            className="mt-3 space-y-2"
            onSubmit={event => {
              event.preventDefault();
              setSubmitted(true);
              if (!VersionNumber.safeParse(versionNumber).success) return;
              void run(async () => {
                const created = await api.presets.create({
                  body: {
                    title: title.trim(),
                    from: null,
                    versionNumber: versionNumber.trim(),
                  },
                });
                await refreshPresets(queryClient);
                await navigate({
                  to: "/presets/$slug/edit",
                  params: { slug: created.preset.slug },
                  search: { draft: created.draftId },
                });
              });
            }}
          >
            <input
              value={title}
              onChange={event => setTitle(event.target.value)}
              maxLength={PRESET_TITLE_MAX_CHARACTERS}
              placeholder="Preset title"
              aria-label="Preset title"
              className="h-10 w-full rounded-xl bg-foreground/5 px-3 text-sm outline-none focus:bg-foreground/[0.07]"
            />
            <label className="flex h-10 items-center gap-2 rounded-xl bg-foreground/5 px-3 text-sm focus-within:bg-foreground/[0.07]">
              <span className="shrink-0 text-muted-foreground">Version number</span>
              <span className="ml-1 text-muted-foreground">v</span>
              <input
                {...numberLimit.field}
                value={versionNumber}
                onChange={event => {
                  setVersionNumber(event.target.value);
                  setSubmitted(false);
                }}
                inputMode="decimal"
                placeholder="1"
                className="-ml-1.5 min-w-0 flex-1 bg-transparent tabular-nums outline-none placeholder:text-muted-foreground/50"
              />
            </label>
            {numberProblem && <p className="px-1 text-xs text-destructive">{numberProblem}</p>}
            <p className="truncate px-1 py-1.5 text-xs text-muted-foreground">
              pax.co/presets/<span className="text-foreground">{slug}</span>
            </p>
            <button
              type="submit"
              // Ready only with both a title and a whole version number, like "1" or "0.1".
              disabled={busy || !title.trim() || !VersionNumber.safeParse(versionNumber).success}
              className={`h-9 w-full rounded-full bg-foreground text-sm font-medium text-background hover:bg-foreground/85 disabled:opacity-40 ${panelMotion} ${press} ${focusHaloFlush}`}
            >
              {busy ? "Creating…" : "Create from scratch"}
            </button>
            {problem && <p className="text-xs text-destructive">{problem}</p>}
          </form>
        )}
      </div>
    </ScreenOverlay>
  );
}
